import {
  Badge,
  Button,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu
} from '@mui/material'
import React, { type Dispatch, type SetStateAction, useMemo } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'

import { MUIExternalLink } from 'components/common/MUIExternalLink'
import Icon from 'components/Icon'
import { SubNavItem } from 'components/navigation/Nav/SubNavItem'
import { useNowRunningContext } from 'components/now-running/contexts/NowRunningContext'
import { useAnchorElement } from 'hooks/use-anchor-element'
import { muiTheme } from 'mui-theme'
import { colors } from 'mui-theme/theme'
import theme from 'theme'

import type { NavItemConfig } from 'components/navigation/Nav/use-gremlin-navigation'

function getListItemButtonComponent(navItem: NavItemConfig) {
  if (navItem.subNav) {
    return Button
  }

  if (navItem.isExternalLink) {
    return MUIExternalLink
  }

  return RouterLink
}

export const NavItem: React.FC<{
  navItem: NavItemConfig
  selectedNavItem: string | null
  setSelectedNavItem: Dispatch<SetStateAction<string | null>>
}> = ({ navItem, selectedNavItem, setSelectedNavItem }) => {
  const navigate = useNavigate()
  const { totalActiveTests } = useNowRunningContext()

  const {
    anchorElement,
    handleAnchorElementClick,
    handleAnchorElementClose,
    isOpen: isSubNavOpen
  } = useAnchorElement()

  // since component is either button or Link, only provide one of these
  const clickProps = useMemo(() => {
    if (navItem.subNav) {
      return {
        onClick: (e) => {
          const { name, route, state, subNav } = navItem

          setSelectedNavItem(name)

          if (subNav && !anchorElement) {
            handleAnchorElementClick(e)
          } else if (route) {
            navigate(route, { state })
          }
        }
      }
    }

    if (navItem.isExternalLink && navItem.href) {
      return { href: navItem.href }
    }

    return { to: navItem.route, state: navItem.state }
  }, [
    anchorElement,
    handleAnchorElementClick,
    navItem,
    navigate,
    setSelectedNavItem
  ])

  const isListIconSpinning =
    navItem.name === 'now-running' && totalActiveTests > 0

  // We only display up to 99 active reliability tests
  const totalActiveTestsForDisplay =
    totalActiveTests <= 99 ? totalActiveTests : '99+'

  const ListItemButtonComponent = getListItemButtonComponent(navItem)

  const { sx: listItemSx, ...listItemProps } = navItem.props ?? {}

  return (
    <ListItem disablePadding sx={navItem.style}>
      <ListItemButton
        dense
        component={ListItemButtonComponent}
        data-link-active={navItem.isActive}
        data-testid={`nav-${navItem.name}`}
        data-track-action={`open-${navItem.name}`}
        data-track-category="main-navigation"
        data-track-on="click"
        disabled={navItem.disabled}
        sx={{
          cursor: navItem.disabled ? 'not-allowed' : 'pointer',
          color: navItem.disabled
            ? muiTheme.palette.grey[600]
            : muiTheme.palette.common.white,
          flexDirection: 'column',
          justifyContent: 'center',
          paddingY: 1,
          paddingX: 0.5,
          svg: {
            opacity: 0.5,
            color: muiTheme.palette.common.white
          },

          [muiTheme.breakpoints.up('md')]: {
            flexDirection: 'row',
            justifyContent: 'initial'
          },

          '&:hover': {
            color: navItem.disabled
              ? muiTheme.palette.grey[600]
              : muiTheme.palette.common.white,
            svg: {
              opacity: 1,
              color: colors.secondary.blue[100]
            }
          },

          ...(navItem.isActive && {
            color: muiTheme.palette.common.white,
            background: colors.primary[650],
            svg: {
              opacity: 1,
              color: muiTheme.palette.common.white
            },

            // this is needed to override the mui styling defaults
            '&:hover, &:focus': {
              color: navItem.disabled
                ? muiTheme.palette.grey[600]
                : muiTheme.palette.common.white,
              background: colors.primary[650]
            },

            '&:hover': {
              svg: {
                color: colors.secondary.blue[100]
              }
            }
          }),

          ...(listItemSx ?? {})
        }}
        {...clickProps}
        {...(listItemProps ?? {})}
      >
        {navItem.icon && (
          <ListItemIcon
            sx={{
              justifyContent: 'center',
              color: navItem.disabled
                ? muiTheme.palette.grey[600]
                : muiTheme.palette.common.white
            }}
          >
            <Icon
              css={{
                width: theme.fontSizes[4],
                height: theme.fontSizes[4],
                transition: 'opacity 150ms ease 0s',
                ...navItem.iconStyle
              }}
              data-test-navigation-notification={navItem.showNotification}
              icon={navItem.icon}
              spinning={isListIconSpinning}
            />
          </ListItemIcon>
        )}
        <ListItemText
          primary={navItem.text}
          primaryTypographyProps={{ variant: 'body2' }}
          sx={{
            '& .MuiListItemText-primary': {
              lineHeight: muiTheme.spacing(2),
              textAlign: 'center',
              fontSize: theme.fontSizes[0],

              [muiTheme.breakpoints.up('md')]: {
                textAlign: 'left',
                fontSize: theme.fontSizes[1]
              }
            }
          }}
        />
        {navItem.isExternalLink && (
          <ListItemIcon
            sx={{
              display: 'none',
              [muiTheme.breakpoints.up('md')]: {
                display: 'flex',
                justifyContent: 'center'
              }
            }}
          >
            <Icon icon="arrowUpRightFromSquare" />
          </ListItemIcon>
        )}
        {navItem.showBadgeCount && (
          <ListItemIcon
            sx={{
              justifyContent: 'center',
              padding: 1
            }}
          >
            <Badge
              showZero
              badgeContent={totalActiveTestsForDisplay}
              color={totalActiveTests > 0 ? 'warning' : 'secondary'}
            />
          </ListItemIcon>
        )}
        {navItem.showNotification && (
          <ListItemIcon
            sx={{
              justifyContent: 'flex-end'
            }}
          >
            <Badge
              badgeContent=""
              color="warning"
              sx={{
                left: muiTheme.spacing(1),
                bottom: muiTheme.spacing(1)
              }}
              variant="dot"
            />
          </ListItemIcon>
        )}
        {navItem.subNav && (
          <>
            <ListItemIcon
              sx={{
                display: 'none',
                [muiTheme.breakpoints.up('md')]: {
                  display: 'flex',
                  justifyContent: 'center'
                }
              }}
            >
              <Icon icon="chevronRight" />
            </ListItemIcon>

            {navItem.name === selectedNavItem && (
              <Menu
                anchorEl={anchorElement}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'right'
                }}
                open={isSubNavOpen}
                transformOrigin={{
                  vertical: 'center',
                  horizontal: 'left'
                }}
                onClose={handleAnchorElementClose}
              >
                {navItem.subNav.map((subNavItem) => {
                  if (!subNavItem.enabled) {
                    return null
                  }

                  return (
                    <SubNavItem
                      key={subNavItem.name}
                      handleClose={handleAnchorElementClose}
                      subNavItem={subNavItem}
                    />
                  )
                })}
              </Menu>
            )}
          </>
        )}
      </ListItemButton>
    </ListItem>
  )
}
